import { default as classNames } from "classnames";
import { Form, FormikProvider, useFormik } from "formik";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import { TEXT_RELOAD_DELAY } from "constant";
import features from "features";
import { getCurrentDate } from "helpers/currentDate";
import { RootStateInterface } from "reducer";
import { AuthStateInterface } from "scenes/AuthPage/interfaces";

import CommonButton from "components/buttons/CommonButton/CommonButton";
import PrimaryButton from "components/buttons/PrimaryButton/PrimaryButton";
import FormField from "components/inputs/FormField/FormField";
import ModalWrapper from "components/modals/ModalWrapper/ModalWrapper";

import CopyIcon from "assets/icons/copy.svg";
import ReloadIcon from "assets/icons/reload.svg";

import styles from "./share-project-modal.module.scss";

const validationSchema = Yup.object().shape({
  date: Yup.string().required("Date  is required"),
  time: Yup.string().required("Time is required")
});

const ShareProjectModal = () => {
  const dispatch = useDispatch();

  const { jwtData, isLoading } = useSelector<
    RootStateInterface,
    AuthStateInterface
  >((state) => state.auth);
  const { id: project_id } = useParams();

  const [shareLink, setShareLink] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  const onSubmit = useCallback(
    (val) => {
      const localDatetime = new Date(`${val.date}T${val.time}:00`);
      const utcDatetime = new Date(localDatetime.toISOString());
      const datetimeString = utcDatetime.toISOString();

      const timestampInMilliseconds = new Date(datetimeString).getTime();
      const expired_at = Math.floor(timestampInMilliseconds / 1000);

      const { org_id, dep_id } = jwtData;

      dispatch(
        features.auth.actions.shareTokenRequest({
          params: { org_id, dep_id },
          fields: { expired_at, project_id: Number(project_id) },
          onSuccess: (res) => {
            setShareLink(
              `${window.location.origin}${window.location.pathname}?share-token=${res.message}`
            );
          }
        })
      );
    },
    [dispatch, jwtData, project_id]
  );

  const formik = useFormik({
    validateOnChange: true,
    initialValues: {
      date: "",
      time: ""
    },
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    onSubmit
  });

  const onDecline = useCallback(() => {
    dispatch(features.modal.actions.hideModal());
  }, [dispatch]);

  const onCopyToClipboard = useCallback(() => {
    navigator.clipboard
      .writeText(shareLink)
      .then(() => {
        setIsCopied(true);
        setShareLink(undefined);
      })
      .catch(() => {
        setIsCopied(false);
      });

    setTimeout(() => {
      setIsCopied(false);
    }, TEXT_RELOAD_DELAY);
  }, [shareLink]);

  return (
    <ModalWrapper className={styles["modal-container"]}>
      <div className={styles["form-modal"]}>
        <div className={styles["form-modal__text-container"]}>
          <h2 className={styles["form-modal__title"]}>Share project</h2>
          <h4 className={styles["form-modal__description"]}>
            Specify the date and time when the project link is valid and
            generate the link
          </h4>
        </div>
        <FormikProvider value={formik}>
          <Form className={styles["form"]}>
            <FormField name="date" type="date" min={getCurrentDate()} />
            <FormField name="time" type="time" />
            <div className={styles["form__values-wrapper"]}>
              {shareLink ? (
                <CommonButton
                  onClick={onCopyToClipboard}
                  icon={CopyIcon}
                  isSmall
                >
                  <span className={styles["hidden-text"]}>{shareLink}</span>
                </CommonButton>
              ) : (
                <CommonButton
                  icon={ReloadIcon}
                  isSmall
                  type="submit"
                  isLoading={isLoading}
                >
                  Generate link
                </CommonButton>
              )}
              <label
                className={classNames(styles["copied-message"], {
                  [styles["copied-message--active"]]: isCopied
                })}
              >
                Link copied in the clipboard!
              </label>
            </div>
            <div className={styles["form-modal__controls-wrapper"]}>
              <PrimaryButton textSize={14} onClick={onDecline}>
                Done
              </PrimaryButton>
            </div>
          </Form>
        </FormikProvider>
      </div>
    </ModalWrapper>
  );
};
export default ShareProjectModal;
